import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { NavbarUser, Footer } from '../../containers';
import { useTranslation } from 'react-i18next';
import './bookedeventsdetails.css';
import api from "../../api/axios";

const BookedEventsDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // Expecting location.state.eventid OR build eventid from props
  const { eventid } = location.state || {};
  const [groups, setGroups] = useState([]);
  const [pictureSrc, setPictureSrc] = useState("");
  const [eventDetails, setEventDetails] = useState(null);
  const [tableImageSrc, setTableImageSrc] = useState(null);
  const [cardAngles, setCardAngles] = useState([]);

  const ovalRef = useRef(null);
  const [ovalRadius, setOvalRadius] = useState(220);
  const [ovalScaleY, setOvalScaleY] = useState(0.6);

  const messagesContainerRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [groupMembers, setGroupMembers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [groupsLoaded, setGroupsLoaded] = useState(false);

  // Responsive State
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [activeTooltip, setActiveTooltip] = useState(null);

  const hobbyMapping = {
    1: "Lesen",
    2: "Sport",
    3: "Musik",
    4: "Kochen",
    5: "Reisen",
    6: "Gärtnern",
    7: "Malen/Zeichnen",
    8: "Handwerk/Basteln",
    9: "Fotografie",
    10: "Tanzen",
    11: "Schreiben"
  };

  useEffect(() => {
    const fetchMe = async () => {
      try {
        const res = await api.get("/user/auth/me");
        setCurrentUser(res.data);
      } catch (err) {
        console.error("Auth error", err);
      }
    };

    fetchMe();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!eventid) return;

    fetchGroupMembers(eventid);
    fetchEventDetails(eventid);
  }, [eventid]);

  const fetchGroupMembers = async (eventid) => {
    try {
      const res = await api.post("/user/matching/groupmembers", {
        eventid
      });

      console.log("Details: Fetched Group Members:", res.data.members);
      setGroupMembers(res.data.members);
      setGroupsLoaded(true);
    } catch (err) {
      console.error("Error fetching group members:", err);
      setGroupsLoaded(true); // done loading, but no groups found
    }
  };

  const fetchEventDetails = async (eventid) => {
    try {
      const res = await api.post(
        "/user/event/details",
        { eventid }
      );
      setEventDetails(res.data);
    } catch (err) {
      console.error("Error fetching event details:", err);
    }
  };

  useEffect(() => {
    const fetchPicture = async () => {
      try {
        const bgPictureId = 22;
        const responseBg = await api.get(`/user/getwebsitepicture/${bgPictureId}`);
        setPictureSrc(responseBg.data.picture);

        const tablePictureId = 21;
        const responseTable = await api.get(`/user/getwebsitepicture/${tablePictureId}`);
        setTableImageSrc(responseTable.data.picture);
      } catch (err) { console.error(err); }
    };
    fetchPicture();
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  }, []);

  const fetchMessages = async () => {
    try {
      const res = await api.post('/user/chat/fetch', { eventid });
      setMessages(res.data);
    } catch (err) { console.error(err); }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      await api.post("/user/chat/send", {
        eventid,
        message: newMessage
      });

      setNewMessage("");
      fetchMessages();
    } catch (err) {
      console.error(err);
    }
  };

  const groupsReady = groupsLoaded && groupMembers.length > 0;

  useEffect(() => {
    if (!eventid || !groupsReady) return;

    // Fetch messages initially
    fetchMessages();

    // Poll every 3 seconds
    const interval = setInterval(fetchMessages, 3000);

    return () => clearInterval(interval); // cleanup
  }, [eventid, groupsReady]);

  useEffect(() => {
    // Only run oval calculation on desktop
    if (isMobile) return;

    const calculateOval = () => {
      const container = ovalRef.current;
      if (!container) return;

      const w = container.clientWidth;
      const h = container.clientHeight;
      const cardWidth = 140; // MUST match .user-card width

      const a = Math.max(40, (w / 2) - (cardWidth / 2));
      const b_visual = Math.max(40, (h / 2) - (cardWidth / 2));

      const scaleY = b_visual / a;
      setOvalRadius(a);
      setOvalScaleY(scaleY);

      // --- Equidistant Angle Calculation ---
      const N = groupMembers.length;
      console.log("Details: N in calculateOval:", N);
      if (N === 0) {
        setCardAngles([]);
        return;
      }

      // 1. Numerical integration to map t (0..2PI) to distance s
      const steps = 360;
      const dists = [0];
      let totalLen = 0;

      for (let i = 1; i <= steps; i++) {
        const t1 = (2 * Math.PI * (i - 1)) / steps;
        const t2 = (2 * Math.PI * i) / steps;

        const x1 = a * Math.cos(t1);
        const y1 = b_visual * Math.sin(t1);

        const x2 = a * Math.cos(t2);
        const y2 = b_visual * Math.sin(t2);

        const d = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
        totalLen += d;
        dists.push(totalLen);
      }

      const segmentLen = totalLen / N;
      const newAngles = [];

      for (let i = 0; i < N; i++) {
        const targetDist = segmentLen * i;

        let stepIdx = 0;
        while (stepIdx < dists.length && dists[stepIdx] < targetDist) {
          stepIdx++;
        }

        let t = 0;
        if (stepIdx === 0) t = 0;
        else {
          const dLower = dists[stepIdx - 1];
          const dUpper = dists[stepIdx];
          const fraction = (targetDist - dLower) / (dUpper - dLower);
          t = (2 * Math.PI * (stepIdx - 1 + fraction)) / steps;
        }

        newAngles.push(t); // in radians
      }

      console.log("Details: Calculated Angles:", newAngles);
      setCardAngles(newAngles);
    };

    calculateOval();
    window.addEventListener("resize", calculateOval);
    return () => window.removeEventListener("resize", calculateOval);
  }, [groupMembers, isMobile]);

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);


  const formatEventDate = (dateString) => {
    try {
      const options = { timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit' };
      const dateTimeFormat = new Intl.DateTimeFormat('de-DE', options);
      return dateTimeFormat.format(new Date(dateString));
    } catch (e) { return dateString; }
  };

  const InfoIcon = ({ instagramm, aboutme, memberId }) => {
    const isOpen = activeTooltip === memberId;
    return (
      <span
        className={`info-icon ${isOpen ? 'active' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          setActiveTooltip(isOpen ? null : memberId);
        }}
      >
        ℹ️
        <span className={`info-tooltip ${isOpen ? 'visible' : ''}`}>
          <strong>{t('BookedEventsDetailsInstagram')}</strong> {instagramm || "—"} <br />
          <strong>{t('BookedEventsDetailsAboutMe')}</strong> {aboutme || "—"}
        </span>
      </span>
    );
  };

  return (
    <div
      className="wrapper-myevents"
      style={{ backgroundImage: pictureSrc ? `url(${pictureSrc})` : "none" }}
    >
      <NavbarUser />

      <div className="myevents-site" onClick={() => setActiveTooltip(null)}>
        <div className={`myevents-layout ${isMobile ? 'mobile-layout' : ''}`}>

          {/* --- LEFT SIDE / MAIN CONTENT --- */}
          <div className="myevents-left">

            {/* If Mobile: Event Info shows FIRST, above everything */}
            {isMobile && eventDetails && (
              <div className="mobile-event-info">
                <h2>{eventDetails.lokal}</h2>
                <div className="mobile-event-detail">
                  <span className="mobile-event-icon">📍</span>
                  <span>{eventDetails.address}</span>
                </div>
                <div className="mobile-event-detail">
                  <span className="mobile-event-icon">📅</span>
                  <span>{formatEventDate(eventDetails.date)}</span>
                </div>
                <div className="mobile-event-detail">
                  <span className="mobile-event-icon">🕒</span>
                  <span>{eventDetails.time}</span>
                </div>
              </div>
            )}

            {isMobile && !eventDetails && groupsLoaded && !groupsReady && (
              <div className="mobile-event-info">
                <h2>⏳ {t('BookedEventsDetailsGroupsNotCreated')}</h2>
                <p> {t('BookedEventsDetailsGroupsNotCreatedText1')} <strong> {t('BookedEventsDetailsGroupsNotCreatedText4')}</strong> {t('BookedEventsDetailsGroupsNotCreatedText2')}</p>
                <p> {t('BookedEventsDetailsGroupsNotCreatedText3')}</p>
              </div>
            )}

            {/* If Desktop: Oval Table View */}
            {!isMobile ? (
              <div className="oval-container" ref={ovalRef}>

                {/* Table Image Background */}
                {tableImageSrc && (
                  <img
                    src={tableImageSrc}
                    alt="Table"
                    className="table-background-image"
                  />
                )}

                {/* Center info (Desktop only) */}
                <div className="center-event-info">
                  {eventDetails ? (
                    <>
                      <div className="center-title">{eventDetails.lokal}</div>
                      <div className="center-line">{eventDetails.address}</div>
                      <div className="center-line">
                        {formatEventDate(eventDetails.date)} · {eventDetails.time}
                      </div>
                    </>
                  ) : groupsLoaded && !groupsReady ? (
                    <>
                      <div className="center-title">⏳ {t('BookedEventsDetailsGroupsNotCreated')}</div>
                      <div className="center-line">
                        {t('BookedEventsDetailsGroupsNotCreatedText1')} <strong> {t('BookedEventsDetailsGroupsNotCreatedText4')}</strong> {t('BookedEventsDetailsGroupsNotCreatedText2')}
                      </div>
                    </>
                  ) : (
                    <div className="center-line"> {t('BookedEventsDetailsLoading')}</div>
                  )}
                </div>

                {/* Users distributed around the table */}
                <div
                  className="oval"
                  style={{
                    transform: `translate(-50%, -50%) scaleY(${ovalScaleY})`,
                  }}
                >
                  {groupMembers.length === 0 && (
                    <p style={{ color: "white", position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>No users assigned yet</p>
                  )}

                  {groupMembers.map((member, index) => {
                    // Use calculated angle if available, else fallback to uniform
                    const angle = cardAngles[index] !== undefined
                      ? cardAngles[index]
                      : (2 * Math.PI / groupMembers.length) * index;

                    const x = Math.cos(angle) * ovalRadius;
                    const y = Math.sin(angle) * ovalRadius;

                    return (
                      <div
                        key={`${member.userid}-${index}`} // Fix: Ensure unique key if userids are duplicated
                        className="item-wrapper"
                      >
                        <div
                          className="user-card"
                          style={{
                            position: "absolute",
                            left: `calc(50% + ${x}px)`,
                            top: `calc(50% + ${y}px)`,
                            transform: `translate(-50%, -50%) scaleY(${1 / ovalScaleY})`,
                          }}
                        >
                          {member.profilbild && (
                            <img
                              src={`data:image/jpg;base64,${member.profilbild}`}
                              alt={`${member.vorname} ${member.nachname}`}
                            />
                          )}
                          <div className="user-card-header">
                            <strong>
                              {member.vorname} {member.nachname}
                            </strong>
                            <InfoIcon
                              instagramm={member.instagramm}
                              aboutme={member.aboutme}
                              memberId={member.userid}
                            />
                          </div>
                          <div style={{ fontSize: "13px", marginTop: "6px" }}>
                            {member.alter ? `${member.alter} Jahre` : ""}
                          </div>
                          <div style={{ marginTop: '6px', fontSize: '12px' }}>
                            {member.hobbiesv2 && member.hobbiesv2.length > 0
                              ? member.hobbiesv2.map(hobbyId => hobbyMapping[hobbyId]).join(', ')
                              : 'Keine Hobbys'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              /* Mobile: Grid View of User Cards */
              <div className="mobile-user-grid">
                {groupMembers.map((member) => (
                  <div key={member.userid} className="user-card mobile-card">
                    {member.profilbild && (
                      <img
                        src={`data:image/jpg;base64,${member.profilbild}`}
                        alt={`${member.vorname} ${member.nachname}`}
                      />
                    )}
                    <div className="user-card-header">
                      <strong>
                        {member.vorname} {member.nachname}
                      </strong>
                      <InfoIcon
                        instagramm={member.instagramm}
                        aboutme={member.aboutme}
                        memberId={member.userid}
                      />
                    </div>
                    <div style={{ fontSize: "13px", marginTop: "6px" }}>
                      {member.alter ? `${member.alter} Jahre` : ""}
                    </div>
                    <div style={{ marginTop: '6px', fontSize: '12px' }}>
                      {member.hobbiesv2 && member.hobbiesv2.length > 0
                        ? member.hobbiesv2.map(hobbyId => hobbyMapping[hobbyId]).join(', ')
                        : 'Keine Hobbys'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* --- RIGHT SIDE / CHAT --- */}
          <div className="myevents-right">
            <div className="chatbox">
              <div className="chat-header">
                <h3> {t('BookedEventsDetailsEventChat')}</h3>
              </div>
              <div className="messages" ref={messagesContainerRef}>
                {!groupsReady ? (
                  <div className="chat-disabled-message">
                    <p>⏳ {t('BookedEventsDetailsChatDisabled')}</p>
                    <p style={{ fontSize: '12px', marginTop: '8px', opacity: 0.7 }}>{t('BookedEventsDetailsGroupsNotCreatedText1')} <strong>{t('BookedEventsDetailsGroupsNotCreatedText4')}</strong> {t('BookedEventsDetailsGroupsNotCreatedText2')}</p>
                  </div>
                ) : (
                  messages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`message ${msg.userid === currentUser?.userid ? "self" : "other"
                        }`}
                    >
                      <div className="message-sender">{msg.vorname}</div>
                      <div className="message-text">{msg.message}</div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="chat-input-container">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={groupsReady ? t('BookedEventsDetailsChatDisabledText4') : t('BookedEventsDetailsChatDisabledText5')}
                onKeyDown={(e) => e.key === 'Enter' && groupsReady && sendMessage()}
                disabled={!groupsReady}
              />
              <button onClick={sendMessage} disabled={!groupsReady}>
                {t('BookedEventsDetailsSend')}
              </button>
            </div>
          </div>

        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BookedEventsDetails;